import { useTranslation } from 'react-i18next';

type Turn = { role: string; text: string; offset: number | null };

/** Normalise the provider `messages` array (or the flat `transcript` string) into
 *  ordered turns. ElevenLabs roles are 'agent' (assistant) and 'user' (caller). */
function toTurns(messages: unknown, transcript: string | null): Turn[] {
  if (Array.isArray(messages)) {
    return messages
      .map((m) => {
        const r = (m ?? {}) as Record<string, unknown>;
        const text = String(r.message ?? r.text ?? '').trim();
        const secs = r.time_in_call_secs;
        return {
          role: String(r.role ?? ''),
          text,
          offset: typeof secs === 'number' ? secs : null,
        };
      })
      .filter((tn) => tn.text);
  }
  if (transcript) {
    return transcript
      .split('\n')
      .map((line) => {
        const idx = line.indexOf(':');
        if (idx === -1) return { role: '', text: line.trim(), offset: null };
        return { role: line.slice(0, idx).trim(), text: line.slice(idx + 1).trim(), offset: null };
      })
      .filter((tn) => tn.text);
  }
  return [];
}

function fmtOffset(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Chat-bubble transcript: the caller on the right (green), the assistant on the
 *  left (grey), each with a mono speaker · time meta line. */
export function CallTranscript({
  messages,
  transcript,
  callerName,
}: {
  messages: unknown;
  transcript: string | null;
  callerName?: string | null;
}) {
  const { t } = useTranslation();
  const turns = toTurns(messages, transcript);
  if (turns.length === 0) {
    return <div className="text-xs text-muted-foreground">{t('calls.noTranscript')}</div>;
  }

  const callerLabel = callerName || t('calls.speakerCaller');
  const assistantLabel = t('calls.speakerAssistant');

  return (
    <div className="flex flex-col gap-2.5">
      {turns.map((turn, i) => {
        const isCaller = turn.role === 'user';
        return (
          <div
            key={i}
            className={`flex max-w-[85%] flex-col ${isCaller ? 'items-end self-end' : 'items-start self-start'}`}
          >
            <div className="mb-0.5 font-mono text-[10px] text-muted-foreground">
              {isCaller ? callerLabel : assistantLabel}
              {turn.offset != null ? ` · ${fmtOffset(turn.offset)}` : ''}
            </div>
            <div
              className={`rounded-2xl px-3 py-2 text-sm leading-relaxed text-foreground ${
                isCaller ? 'rounded-br-sm bg-accent' : 'rounded-bl-sm bg-muted'
              }`}
            >
              {turn.text}
            </div>
          </div>
        );
      })}
    </div>
  );
}
